/**
 * Image and Image List Fields
 * @author MattJS (matt@yext.com)
 */
goog.provide('yext.location.image.Image');
goog.provide('yext.location.image.ImageModel');
goog.provide('yext.location.image.ImageField');
goog.provide('yext.location.image.ImageListField');
goog.provide('yext.location.image.ImageUploader');
goog.provide('yext.location.image.ImageGallery');

goog.require('yext');
goog.require('yext.dam.assetselector.events');
goog.require('yext.dam.assetselector.types');
goog.require('yext.dialog');
goog.require('yext.fileupload');
goog.require('yext.image.Constraints');
goog.require('yext.location.helpers');
goog.require('yext.textfield');
goog.require('yext.textfield.TextField');

/**
 * Image properties that are not found on sub-fields
 *
 * @constructor
 * @param {!Object} data Untyped image data
 */
yext.location.image.ImageModel = function(data) {
  this.url = data.url;
  this.sourceUrl = data.sourceUrl;
  this.indeterminate = data.indeterminate || false;
  this.destinations = data.destinations || [];
};

/**
 * Set the indeterminate flag for this field. The model will emit an event if it is updated
 *
 * @param {boolean} indeterminate
 */
yext.location.image.ImageModel.prototype.setIndeterminate = function(indeterminate) {
  var changed = false;
  if (indeterminate != this.indeterminate) {
    changed = true;
  }
  if (changed) {
    this.indeterminate = indeterminate;
    this.emitUpdate();
  }
};

/**
 * Set the photo destinations list. The model will emit an event if it is updated
 *
 * @param {!Array} List of ImageData.Destination enum values as strings
 */
yext.location.image.ImageModel.prototype.setDestinations = function(destinations) {
  var changed = false;
  if (this.destinations.length != destinations.length) {
    changed = true;
  } else {
    for (var i = 0; i < destinations.length; i++) {
      if (destinations[i] != this.destinations[i]) {
        changed = true;
        break;
      }
    }
  }
  if (changed) {
    this.destinations = destinations;
    this.emitUpdate();
  }
};

/**
 * Store a file object that will be uploaded with the request
 * @param {File} file File object for this image
 */
yext.location.image.ImageModel.prototype.setFile = function(file) {
  if (file instanceof File) {
    this.file = file;
  } else {
    throw new TypeError('Only File objects are accepted to ImageModel#setFile');
  }
};

/**
 * Set base64 encoded cropped image data on the instance
 * @param {string} base64Data
 */
yext.location.image.ImageModel.prototype.setBase64Data = function(base64Data) {
  this.base64Data = base64Data;
};

/**
 * Emit an update using jQuery pub/sub and the instance as context
 */
yext.location.image.ImageModel.prototype.emitUpdate = function() {
  $(this).trigger('updated');
};

/**
 * @return {!Object} Un-typed copy of this model with string-keyed properties
 */
yext.location.image.ImageModel.prototype.copy = function() {
  let copy = {
    'url': this.url,
    'indeterminate': this.indeterminate,
    'destinations': this.destinations.slice(),
  };

  if (this.sourceUrl) {
    copy['sourceUrl'] = this.sourceUrl;
  }

  return copy;
};

/**
 * Image Field. This supports a single image or an image as part of a gallery
 *
 * TODO(mattjs) Additional work needed to support single image
 * TODO(mattjs) Bind indeterminate checkbox
 *
 * @constructor
 * @param {!jQuery} $ctx Element context for the Image Field
 */
yext.location.image.Image = function($ctx) {
  this.$ctx = $ctx;
  this.$field = $ctx.find('.js-image-field').addBack().filter('.js-image-field');
  this.$destinations = this.$field.find('.js-image-destinations');
  // This is outside the standard 'field' context, but to support single and list at the same time
  // we use the extended context, where the checkbox can be found
  this.$imageSelectedCheckbox = $ctx.find('.js-image-selected-checkbox');
  this.$warningIcon = $ctx.find('.js-image-warning-icon');
  // Map the instance to the element
  this.$field.data('image', this);

  this.model = this.buildModel();

  this.fieldId = this.$field.data('fieldId');

  // These are the potential string sub-fields of an image field
  this.alternateText = undefined;
  this.caption = undefined;
  this.clickThroughUrl = undefined;
  this.details = undefined;
  this.width = undefined;
  this.height = undefined;

  this.constructStringSubFields();
  this.render();
  this.bindDestinations();
  this.bindDelete();
  this.bindImageSelectedCheckbox();
  this.bindUpdates();
  this.bindWarningIconHover();
};

/**
 * @return {boolean} True always in single edit, this reflects the value of the image selected
 *                   checkbox in bulk edit.
 */
yext.location.image.Image.prototype.isSelected = function() {
  if (this.$imageSelectedCheckbox.length) {
    return this.$imageSelectedCheckbox.prop('checked');
  }
  return true;
};

/**
 * Returns true if image is indeterminate
 */
yext.location.image.Image.prototype.isIndeterminate = function() {
  return this.model.indeterminate;
};

/**
 * Generate an Image from an Asset
 * @param {yext.dam.asset.asset} asset
 * @param {string} template Image template as HTML string
 * @return {yext.location.image.Image}
 */
yext.location.image.Image.newImageFromAsset = function(asset, template) {
  // If the image is cropped, we replace url with base64 string
  var url = asset.photoUrl;
  if (asset.croppedImage) {
    url = asset.croppedImage;
  }
  // Template the Image object
  var image = yext.location.image.Image.templateImage(url, template);

  // set source url
  if (asset.sourceUrl) {
    image.model.sourceUrl = asset.sourceUrl;
  }

  // Set alternative formats
  if (asset.croppedImage) {
    image.model.setBase64Data(asset.croppedImage);
  } else if (asset.file) {
    image.model.setFile(asset.file);
  }
  // Set string sub-field values
  if (image.alternateText) {
    image.alternateText.set({
      value: asset.alternateText,
    });
  }
  if (image.caption) {
    image.caption.set({
      value: asset.description,
    });
  }
  if (image.clickThroughUrl) {
    image.clickThroughUrl.set({
      value: asset.clickthroughUrl,
    });
  }
  if (image.details) {
    image.details.set({
      value: asset.details,
    });
  }
  return image;
};

/**
 * Generate an Image from un-typed image data
 * @param {!Object} imageData
 * @param {string} template Image template as HTML string
 * @return {yext.location.image.Image}
 */
yext.location.image.Image.newImage = function(imageData, template) {
  var image = yext.location.image.Image.templateImage(imageData.url, template);

  if (imageData.sourceUrl) {
    image.model.sourceUrl = imageData.sourceUrl;
  }
  if (image.alternateText) {
    image.alternateText.set(imageData.alternateText);
  }
  if (image.caption) {
    image.caption.set(imageData.caption);
  }
  if (image.clickThroughUrl) {
    image.clickThroughUrl.set(imageData.clickThroughUrl);
  }
  if (image.details) {
    image.details.set(imageData.details);
  }
  if (image.destinations) {
    image.model.setDestinations(imageData.destinations);
  }
  if (imageData.indeterminate) {
    image.model.setIndeterminate(imageData.indeterminate);
  }
  return image;
};

/**
 * @param {string} url The image canonical url
 * @param {string} template Image template as HTML string
 */
yext.location.image.Image.templateImage = function(url, template) {
  const dimension = yext.location.image.Image.getDimension(url);
  var html = template
    .replace(/#URL/g, url)
    .replace('#CHILD_IMAGE_URL', yext.getDynamicThumbnailUrl(url, 160, 160));
  if (yext.location.image.Image.isValidSize(dimension.width, dimension.height, 250, 250)) {
    html = html.replace('icon-status-warning', '');
  }

  const image = new yext.location.image.Image($(html));
  image.width = dimension.width;
  image.height = dimension.height;
  return image;
};

/**
 * This method extracts image dimension from PhotoServer formatted url such as
 * 'http://a.mktgcdn.com/KIEwbZ9P1/1920x1080.jpg'.
 *
 * @param {string} url The image url formatted by PhotoServer
 * @return {Object} The image dimension
 */
yext.location.image.Image.getDimension = function(url) {
  if (url) {
    const components = url.split('/');
    const dimensionWithFileExtension = components[components.length - 1];
    const dimensionInString = dimensionWithFileExtension.match(/\d+/g);
    if (dimensionInString) {
      const dimension = dimensionInString.map(Number);
      if (dimension.length === 2) {
        return {
          width: dimension[0],
          height: dimension[1],
        };
      }
    }
  }
  return {};
};

/**
 * Returns whether an image dimension meets the constraint. Note that if either width or its
 * constraint is undefined, it is considered to be valid (same for height).
 * @param {number} width
 * @param {number} height
 * @param {number} widthMinConstraint
 * @param {number} heightMinConstraint
 * @return {boolean} whether an image dimension meets the constraint
 */
yext.location.image.Image.isValidSize = function(
  width,
  height,
  widthMinConstraint,
  heightMinConstraint) {
  return (width === undefined
          || widthMinConstraint === undefined
          || width >= widthMinConstraint)
      && (height === undefined
          || heightMinConstraint === undefined
          || height >= heightMinConstraint);
};

/**
 * @param {string} template HTML template for display mode
 * @param {string} detailTemplate HTML template for image details, i.e. caption, alt-text, etc.
 * @return {!jQuery} element wrapped display for the field
 */
yext.location.image.Image.prototype.getDisplay = function(template, detailTemplate) {
  var html = template
    .replace('#URL', yext.getDynamicThumbnailUrl(this.model.url, 160, 160))
    .replace('#DETAILS', this.getDetailsHtml(detailTemplate));

  if (yext.location.image.Image.isValidSize(this.width, this.height, 250, 250)) {
    html = html.replace('icon-status-warning', '');
  }

  var $html = $(html);
  // Bind the object to the display mode, will drive gallery
  $html.data('image', this.model);
  return $html;
};

/**
 * Templates the details HTML, i.e. caption, alt-text, etc.
 * @param {string} template Details template html
 * @return {string} All details, if any
 */
yext.location.image.Image.prototype.getDetailsHtml = function(template) {
  var details = [];
  if (this.caption && this.caption.value().trim()) {
    var html = template
      .replace('#DETAIL_NAME', yext.msg('Description'))
      .replace('#TID', 'image-description-display')
      .replace('#DETAIL_VALUE',
        this.caption.embed
          ? this.caption.embed.viewMarkupWithDisplayValues(this.caption.value())
          : this.caption.value());
    details.push(html);
  }
  if (this.details && this.details.value().trim()) {
    var html = template
      .replace('#DETAIL_NAME', yext.msg('Details'))
      .replace('#TID', 'image-details-display')
      .replace('#DETAIL_VALUE',
        this.details.embed
          ? this.details.embed.viewMarkupWithDisplayValues(this.details.value())
          : this.details.value());
    details.push(html);
  }
  if (this.alternateText && this.alternateText.value().trim()) {
    var html = template
      .replace('#DETAIL_NAME', yext.msg('Alternate Text'))
      .replace('#TID', 'image-alternate-text-display')
      .replace('#DETAIL_VALUE',
        this.alternateText.embed
          ? this.alternateText.embed.viewMarkupWithDisplayValues(this.alternateText.value())
          : this.alternateText.value());
    details.push(html);
  }
  if (this.clickThroughUrl && this.clickThroughUrl.value().trim()) {
    var html = template
      .replace('#DETAIL_NAME', yext.msg('Click-through Url'))
      .replace('#TID', 'image-clickthrough-url-display')
      .replace('#DETAIL_VALUE',
        this.clickThroughUrl.embed
          ? this.clickThroughUrl.embed.viewMarkupWithDisplayValues(this.clickThroughUrl.value())
          : this.clickThroughUrl.value());
    details.push(html);
  }
  return details.join('');
};

/**
 * Bind delete image
 */
yext.location.image.Image.prototype.bindDelete = function() {
  var self = this;
  this.$field.find('.js-remove-image-button').click(function() {
    self.emitDelete();
  });
};


/**
 * Bind the warning icon message
 */
yext.location.image.Image.prototype.bindWarningIconHover = function() {
  this.$warningIcon.contextualHelp({
    basicText: yext.msg('This photo may be too small to display on certain publishers. ' +
      'Upload a photo that is at least 250x250 and 10 KB to ensure correct display.'),
    event: 'hover',
  });
};

/**
 * Bind the image selected checkbox
 */
yext.location.image.Image.prototype.bindImageSelectedCheckbox = function() {
  var self = this;
  this.$imageSelectedCheckbox.on('click change', function(e) {
    // If the state was indeterminate, set the checkbox to checked
    if (self.model.indeterminate) {
      self.$imageSelectedCheckbox.prop('checked', true);
    }
    self.model.setIndeterminate(false);
    self.emitUpdate();
  });
};

/**
 * Build image model from DOM
 * @return {yext.location.image.ImageModel} Model for image properties not found on a sub-field
 */
yext.location.image.Image.prototype.buildModel = function() {
  var destinations = [];
  if (this.$destinations.length) {
    destinations = this.$destinations.data('selection');
  }

  let imageModel = {
    url: this.$field.data('url'),
    indeterminate: this.$field.data('indeterminate'),
    destinations: destinations,
  };

  if (this.$field.data('sourceUrl')) {
    imageModel.sourceUrl = this.$field.data('sourceUrl');
  }

  return new yext.location.image.ImageModel(imageModel);
};

/**
 * Bind image 'destination' selection. Users can choose from Powerlistings, Faceboook, etc. for
 * where their image will be delivered
 */
yext.location.image.Image.prototype.bindDestinations = function() {
  if (this.$destinations.length) {
    var self = this;
    var $destinations = this.$destinations.find('.js-image-destination');
    $destinations.click(function() {
      $(this).toggleClass('selected').blur();
      var destinations = $destinations
        .filter(function(i, e) {
          return $(e).hasClass('selected');
        })
        .map(function(i, e) {
          return $(e).data('value');
        }).get();
      // Toggle all individually case
      if (destinations.length === $destinations.length) {
        destinations = [];
      }
      self.model.setDestinations(destinations);
    });

    this.$destinations.find('.js-all-image-destinations').click(function() {
      self.model.setDestinations([]);
    });
  }
};

/**
 * Construct TextField modules for the String sub-fields of the image
 */
yext.location.image.Image.prototype.constructStringSubFields = function() {
  var $alternateText = this.$field.find('.js-alternate-text-string-field');
  var $caption = this.$field.find('.js-caption-string-field');
  var $clickThroughUrl = this.$field.find('.js-click-through-url-string-field');
  var $details = this.$field.find('.js-details-string-field');

  if ($alternateText.length) {
    this.alternateText = yext.textfield.createWithUsageType($alternateText, this.fieldId);
  }
  if ($caption.length) {
    this.caption = yext.textfield.createWithUsageType($caption, this.fieldId);
  }
  if ($clickThroughUrl.length) {
    this.clickThroughUrl = yext.textfield.createWithUsageType($clickThroughUrl, this.fieldId);
  }
  if ($details.length) {
    this.details = yext.textfield.createWithUsageType($details, this.fieldId);
  }
};

/**
 * Bind all sources of updates to emitUpdate
 */
yext.location.image.Image.prototype.bindUpdates = function() {
  var self = this;
  $(this.model).on('updated', function() {
    self.onUpdate();
  });
  this.bindSingleStringField(this.alternateText);
  this.bindSingleStringField(this.caption);
  this.bindSingleStringField(this.clickThroughUrl);
  this.bindSingleStringField(this.details);
};

/**
 * @param {yext.textfield.TextField} field The string field to bind
 */
yext.location.image.Image.prototype.bindSingleStringField = function(field) {
  if (field != null) {
    var self = this;
    $(field).on('update.textField', function() {
      self.onUpdate();
    });
  }
};

/**
 * Emit an update using jQuery pub/sub and the instance as context
 */
yext.location.image.Image.prototype.onUpdate = function() {
  this.render();
  this.emitUpdate();
};

/**
 * Deal with any UI changes needed to stay in-sync with the models
 */
yext.location.image.Image.prototype.render = function() {
  var self = this;

  if (this.$destinations.length) {
    var hasDestinations = this.model.destinations.length > 0;
    if (hasDestinations) {
      this.$destinations.find('.js-image-destination').each(function() {
        var $this = $(this);
        var value = $this.data('value');
        $this.toggleClass('selected', self.model.destinations.indexOf(value) !== -1);
      });
    } else {
      this.$destinations.find('.js-image-destination').removeClass('selected');
    }
    this.$destinations.find('.js-all-image-destinations').toggleClass('selected', !hasDestinations);
  }

  if (this.$imageSelectedCheckbox.length) {
    this.$imageSelectedCheckbox.prop('indeterminate', this.model.indeterminate);
  }
};

/**
 * Emit an updated message using jQuery pub/sub and the instance as context
 */
yext.location.image.Image.prototype.emitUpdate = function() {
  $(this).trigger('updated');
};

/**
 * Emit an delete message using jQuery pub/sub and the instance as context
 */
yext.location.image.Image.prototype.emitDelete = function() {
  $(this).trigger('delete');
};

/**
 * Get the data for this module that will deserialize to fields.views.ImageView Java class
 *
 * @return {!Object} Un-typed representation of the Image field data
 */
yext.location.image.Image.prototype.getData = function() {
  var data = this.model.copy();
  if (this.alternateText) {
    data['alternateText'] = this.alternateText.coreData();
  }
  if (this.caption) {
    data['caption'] = this.caption.coreData();
  }
  if (this.clickThroughUrl) {
    data['clickThroughUrl'] = this.clickThroughUrl.coreData();
  }
  if (this.details) {
    data['details'] = this.details.coreData();
  }
  return data;
};

/**
 * @constructor
 * @param {!jQuery} $ctx Element context for the Image Field
 * @param {!Object} options Configurable options for the Field
 */
yext.location.image.ImageField = function($ctx, options) {
  this.$ctx = $ctx;
  this.$field = $ctx.find('.js-image-type-field').addBack().filter('.js-image-type-field');
  this.$edit = this.$field.find('.js-single-image-edit');
  this.$indeterminateMessage = this.$field.find('.js-indeterminate-message');
  this.$selectPhotoWrap = this.$field.find('.js-select-photo-button-wrap');
  this.template = this.$field.find('.js-image-template').html();
  this.viewTemplate = this.$field.find('.js-image-view-template').html();
  this.detailTemplate = this.$field.find('.js-image-detail-template').html();
  this.$errorContainer = this.$field.find('.js-field-error');

  this.uploadUrl = options.uploadUrl;
  this.fieldId = this.$field.data('fieldId');
  this.encodedFieldId = this.$field.data('encodedFieldId');
  this.timestamp = this.$field.data('timestamp');
  this.constraints = new yext.image.Constraints({'container': this.$ctx}).constraints;
  this.aspectRatioId = this.$field.data('aspectRatioId');
  this.aspectRatio = this.$field.data('cropper');
  this.assetsOnly = this.$field.data('assetsOnly');

  this.assetOrOriginal = true;
  this.originalValue = yext.JSON.stringify(this.getJSONData());

  this.image = this.extractImage();
  this.storeValue();
  this.bindImage();
  this.bindResetAll();
  this.bindSelectPhoto();
  this.bindGallery();
};

/**
 * Bind the gallery mode for initial page load
 */
yext.location.image.ImageField.prototype.bindGallery = function() {
  if (this.image) {
    // Bind the model to the DOM
    this.$ctx.find('.js-image-field-view .image-border').data('image', this.image.model);
    new yext.location.image.ImageGallery(this.$ctx.find('.js-image-field-view'));
  }
};

/**
 * Bind the reset link in bulk edit mode
 */
yext.location.image.ImageField.prototype.bindResetAll = function() {
  var self = this;

  this.$ctx.find('.js-reset-all-single-image').click(function() {
    self.setImage();
    self.emitUpdate();
  });
};

/**
 * Bind the select photo button
 */
yext.location.image.ImageField.prototype.bindSelectPhoto = function() {
  var self = this;
  this.$field.find('.js-select-photo-button').click(function() {
    self.showAssetSelector();
  });
};

/**
 * Extract the image from the DOM, if one exists
 * @return {yext.location.image.Image}
 */
yext.location.image.ImageField.prototype.extractImage = function() {
  var $image = this.$field.find('.js-image-field');
  if ($image.length) {
    return $image.data('image');
  }
};

/**
 * Check if the field is indeterminate
 * @return {boolean} true if indeterminate
 */
yext.location.image.ImageField.prototype.isIndeterminate = function() {
  if (this.image) {
    if (yext.isBoolean(this.image.model.indeterminate)) {
      return this.image.model.indeterminate;
    }
  }
  return false;
};

/**
 * Hold a copy of the image module
 */
yext.location.image.ImageField.prototype.storeValue = function() {
  this.storedValue = this.getData();
};

/**
 * Pass along image updates
 */
yext.location.image.ImageField.prototype.bindImage = function() {
  if (this.image) {
    var self = this;
    $(this.image).on('updated', function() {
      self.emitUpdate();
    });

    $(this.image).on('delete', function() {
      self.setImage(); // Clear
      self.emitUpdate();
    });
  }
};

/**
 * @return {!Object} Get the field's data
 */
yext.location.image.ImageField.prototype.getData = function() {
  var data = this.image ? this.image.getData() : {};
  data['timestamp'] = this.timestamp;
  return data;
};


/**
 * Get the data for this module that will deserialize to fields.views.ImageView Java class
 *
 * @return {!Object} Un-typed representation of the Image field data
 */
yext.location.image.ImageField.prototype.getJSONData = function() {
  var data = this.getData();
  if (this.image && this.image.model && this.image.model.base64Data) {
    data['base64Data'] = this.image.model.base64Data;
    data['url'] = undefined;
  }
  return data;
};

/**
 * If there is a file associated with the image, return it here. The image's fieldId is supplied
 * as well for the sake of properly collating files with fields.
 *
 * @return {!Object} Un-typed representation of the Image field data
 */
yext.location.image.ImageField.prototype.getAssociatedFile = function() {
  return (this.image && this.image.model && this.image.model.file)
    ? {'fieldId': this.fieldId, 'file': this.image.model.file}
    : {};
};

/**
 * Return true if the field is empty
 */
yext.location.image.ImageField.prototype.isEmpty = function() {
  return !this.image;
};

/**
 * Reset the field to a stored state
 */
yext.location.image.ImageField.prototype.reset = function() {
  this.set(this.storedValue);
};

/**
 * Render the field given its state
 */
yext.location.image.ImageField.prototype.render = function() {
  this.$errorContainer.hide();
  this.$edit.empty();
  this.$edit.toggle(!this.isIndeterminate());
  this.$indeterminateMessage.toggle(this.isIndeterminate());
  this.$selectPhotoWrap.toggle(this.isEmpty());
  if (!this.isEmpty()) {
    this.$edit.append(this.image.$ctx);
  }
};

/**
 * Set the value of the field
 * @param {!Object} Plain version of the model
 */
yext.location.image.ImageField.prototype.set = function(field) {
  if (field.timestamp) {
    this.timestamp = field.timestamp;
  }

  this.displayImage = undefined;
  this.displayIndeterminate = field.displayIndeterminate;

  var image;
  if (!this.displayIndeterminate) {
    if (field.url) {
      image = yext.location.image.Image.newImage(field, this.template);
      this.displayImage = image;
    } else if (field.displayUrl) {
      var data = $.extend(true, field, {url: field.displayUrl});
      this.displayImage = yext.location.image.Image.newImage(data, this.template);
    }
  } else {
    image = yext.location.image.Image.newImage(field, this.template);
  }
  this.setImage(image);
};

/**
 * Apply asset values to the image.
 */
yext.location.image.ImageField.prototype.applyAsset = function(fieldValues) {
  this.set(fieldValues);
  this.updateAssetOrOriginal(true);
  $(this).trigger('nonAssetClearingUpdate.struct');
  $(this).trigger('nonAssetClearingUpdate.list');
  this.emitUpdate();
};

/**
 * The after save function when this image field is a child of a struct.
 */
yext.location.image.ImageField.prototype.setFromStruct = function(fieldValues, $display, $viewTemplate /* unused */, cannotEdit) {
  $display = $display.find('.js-image-field-view').first();
  this.set(fieldValues);
  this.storeValue();
  $display.empty().append(this.getDisplay(cannotEdit));

  this.assetOrOriginal = true;
  this.originalValue = yext.JSON.stringify(this.getJSONData());
};

/**
 * Emit an updated message using jQuery pub/sub and the instance as context
 */
yext.location.image.ImageField.prototype.emitUpdate = function() {
  $(this).trigger('updated');
  $(this).trigger('update.list');
  $(this).trigger('update.struct');

  this.updateAssetOrOriginal(false);
};

/**
 * Update the asset or original flag.
 */
yext.location.image.ImageField.prototype.updateAssetOrOriginal = function(fromAsset) {
  if (fromAsset || this.originalValue === yext.JSON.stringify(this.getJSONData())) {
    this.assetOrOriginal = true;
  } else {
    this.assetOrOriginal = false;
  }
};

/**
 * Type-ubiquitous function for checking assetOrOriginal flag.
 */
yext.location.image.ImageField.prototype.checkAssetOrOriginalFlag = function() {
  return this.assetOrOriginal;
};

/**
 * Empty out potential text subfields' asset ids.
 */
yext.location.image.ImageField.prototype.clearAsset = function() {
  // TODO (tmeyer): Figure out if we need to / how to clear asset from text subfields.
};

/**
 * Save this field
 * @param {function()} callback
 */
yext.location.image.ImageField.prototype.save = function(callback, overrideUploadUrl, additionalFormData = []) {
  var self = this;
  var images = this.image ? [this.image] : [];
  var actualUploadUrl = overrideUploadUrl || this.uploadUrl;

  new yext.location.image.ImageUploader({
    uploadUrl: actualUploadUrl,
    fieldId: this.fieldId,
    encodedFieldId: this.encodedFieldId,
    images: images,
    fieldData: {
      'timestamp': this.timestamp,
    },
    otherData: this.getAnyFormData().concat(additionalFormData),
  }).upload(function(response) {
    // NOTE: Hack for custom goals
    if (response.responses) {
      callback(undefined, response);
      return;
    }
    if (!response.errors && !response.error && !response.actions) {
      self.reset();
    }
    self.postSave(response);
    self.onUploadSuccess(response, callback);
  }, function(response) {
    self.onUploadError(callback, undefined, response);
  }, true);
};

/**
 * Show the asset selector for selecting images
 */
yext.location.image.ImageField.prototype.showAssetSelector = function() {
  var self = this;
  var selectorUsage = goog.module.get('yext.dam.assetselector.usage');
  var isChildOfStruct = this.$ctx.parents('.js-struct-field').length > 0;
  var parentFieldId = (isChildOfStruct ? this.$ctx.closest('tr').data('fieldId') : this.fieldId) || this.fieldId;

  yext.dam.assetselector.events.show({
    type: yext.dam.assetselector.types.PHOTO,
    includeUsedAssets: false,
    hideUploadImageTab: self.assetsOnly,
    usageType: selectorUsage.forProfileFields(parentFieldId),
  },
  undefined,
  undefined,
  this.constraints);

  // Note: All assets go through the photo cropper before this point.
  // If cropped, asset.croppedImage contains the base64 data of the cropped image.
  $(yext.dam.assetselector.events).on('added.assetselector', function(e, asset) {
    if (asset.variant && !asset.croppedImage) {
      let variant = JSON.parse(asset.variant);

      // Remove asset timestamp before applying - leaving it in would cause submissions
      // to use asset timestamp and be stale.
      delete variant.timestamp;
      self.applyAsset(variant);
    } else if (asset.variant && asset.croppedImage) {
      let variant = JSON.parse(asset.variant);

      // Remove asset timestamp before applying - leaving it in would cause submissions
      // to use asset timestamp and be stale.
      delete variant.timestamp;
      self.applyCroppedAsset(variant, asset.croppedImage);
    } else {
      // Non-asset image
      self.newImageFromAsset(asset);
    }
  });
};

/**
 * Apply asset data, but overwrite asset's url with the croppedImage data.
 * @param {!Object} variant Asset's variant content
 * @param {string} croppedImage Base64Data representing the cropped image
 */
yext.location.image.ImageField.prototype.applyCroppedAsset = function(variant, croppedImage) {
  delete variant.url;
  delete variant.displayUrl;
  variant.croppedImage = croppedImage;

  this.setImage(yext.location.image.Image.newImageFromAsset(variant, this.template));

  this.updateAssetOrOriginal(true);
  $(this).trigger('nonAssetClearingUpdate.struct');
  $(this).trigger('nonAssetClearingUpdate.list');
  this.emitUpdate();
};

/**
 * Generate an Image from an Asset and show it
 * @param {yext.dam.asset.asset} asset
 */
yext.location.image.ImageField.prototype.newImageFromAsset = function(asset) {
  this.setImage(yext.location.image.Image.newImageFromAsset(asset, this.template));
  this.emitUpdate();
};

/**
 * Set the image
 * @param {yext.location.image.Image} image
 */
yext.location.image.ImageField.prototype.setImage = function(image) {
  this.image = image;
  this.bindImage();
  this.render();
};

/**
 * Gets any form data to merge in with the save request
 */
yext.location.image.ImageField.prototype.getAnyFormData = function() {
  var $form = this.$ctx.find('form');
  if ($form.length) {
    return $form.serializeArray();
  }
  return [];
};

/**
 * Set the field value post save and render display
 * @param {!Object} response
 */
yext.location.image.ImageField.prototype.postSave = function(response) {
  $(this).trigger('saved', response);
  if (response['field']) {
    this.set(response['field']);
    this.storeValue();
  }
};

/**
 * Once save is successful, this method is called
 * @param {!Object} response
 * @param {function()} callback On save completed
 */
yext.location.image.ImageField.prototype.onUploadSuccess = function(response, callback) {
  const errorsPresent =
    response['errors']
    && response['errors'].length !== 0
    && typeof response['errors'] === 'string';
  const errorMessage = errorsPresent ? JSON.parse(response['errors'])[0]['message'] : undefined;
  if (errorMessage) {
    this.onUploadError(callback, errorMessage, response);
  } else if (response['error']) {
    this.onUploadError(callback, response['error'][this.fieldId], response);
  } else {
    callback(errorMessage, response);
  }
};

/**
 * When there is an error saving, this method is called
 * @param {!Object} response
 * @param {function()} callback On save completed
 */
yext.location.image.ImageField.prototype.onUploadError = function(callback, msg, response) {
  msg = msg || yext.msg('There was an error while saving your Photo');
  callback(msg, response);
};

/**
 * @return {!jQuery} Display mode HTML for this field
 */
yext.location.image.ImageField.prototype.getDisplay = function(cannotEdit) {
  if (this.displayIndeterminate) {
    return yext.location.helpers.variousContentMessage();
  } else if (!this.displayImage) {
    return yext.location.helpers.emptyFieldMessage(cannotEdit);
  } else {
    var $display = this.displayImage.getDisplay(this.viewTemplate, this.detailTemplate);
    new yext.location.image.ImageGallery($display);
    return $display;
  }
};

/**
 * @return {boolean} True if the field has changed since the last save
 */
yext.location.image.ImageField.prototype.hasChanges = function() {
  if (this.image && this.storedValue.url) {
    if (this.image.model.url == this.storedValue.url) {
      return false;
    }
    // TODO(mattjs) Indeterminate
    return false;
  }
  return this.image != this.storedValue.url;
};

/**
 * clears field
 */
yext.location.image.ImageField.prototype.clear = function() {
  if (this.image) {
    this.image.emitDelete();
  }
};

/**
 * @constructor
 * @param {!jQuery} $ctx Element context for the Image List Field
 * @param {!Object} options Configurable options for the Field
 */
yext.location.image.ImageListField = function($ctx, options) {
  this.$ctx = $ctx;
  this.$field = $ctx.find('.js-image-list-field').addBack().filter('.js-image-list-field');
  this.$list = this.$field.find('.js-image-list-list');
  this.$elidedModeContainer = this.$field.find('.js-elided-mode-container');
  this.$editAllContainer = this.$field.find('.js-image-list-edit-all');
  this.$editAllCheckbox = this.$field.find('.js-image-list-edit-all-checkbox');
  this.template = this.$field.find('.js-image-template').html();
  this.viewTemplate = this.$field.find('.js-image-view-template').html();
  this.detailTemplate = this.$field.find('.js-image-detail-template').html();
  this.$errorContainer = this.$field.find('.js-field-error');

  this.uploadUrl = options.uploadUrl;

  this.elidedModeImageCount = this.getElidedModeImageCountFromDOM();
  this.fieldId = this.$field.data('fieldId');
  this.encodedFieldId = this.$field.data('encodedFieldId');
  this.orderVaries = this.$field.data('orderVaries');
  this.timestamp = this.$field.data('timestamp');
  this.images = this.extractImages();
  this.maxItemsCount = this.$field.data('maxItemsCount');
  this.constraints = new yext.image.Constraints({'container': this.$ctx}).constraints;
  this.aspectRatioId = this.$field.data('aspectRatioId');
  this.aspectRatio = this.$field.data('cropper');
  this.assetsOnly = this.$field.data('assetsOnly');

  this.storeValue();
  this.render();
  this.renderDestinations();
  this.bindAddImage();
  this.bindElidedMode();
  this.bindShowDestinations();
  this.bindMoveImage();
  this.bindEditAllCheckbox();
  this.bindGallery();
};

/**
 * Bind the image gallery for display mode
 */
yext.location.image.ImageListField.prototype.bindGallery = function() {
  var self = this;
  var $ctx = this.$ctx.closest('.image-list-field-full');

  if (this.images.length > 0) {
    // Bind the model to the DOM
    $ctx.find('.js-image-list-field-view .image-border').each(function(idx) {
      $(this).data('image', self.images[idx].model);
    });
  }

  new yext.location.image.ImageGallery($ctx.find('.js-image-list-field-view ul'));
};

/**
 * Bind the image selected checkbox
 */
yext.location.image.ImageListField.prototype.bindEditAllCheckbox = function() {
  if (this.$editAllCheckbox.length) {
    var self = this;

    this.$editAllCheckbox.click(function() {
      if (self.isIndeterminate()) {
        self.$editAllCheckbox.prop('checked', true);
      }
      self.$list.find('input[type="checkbox"]')
        .prop({
          indeterminate: false,
          checked: self.$editAllCheckbox.prop('checked'),
        })
        .trigger('change');
    });
  }
};

/**
 * Sets the checklist checkbox to appropriate state
 */
yext.location.image.ImageListField.prototype.syncCheckList = function() {
  if (this.$editAllCheckbox.length) {
    this.$editAllCheckbox.prop('indeterminate', this.isIndeterminate() && this.allImagesSelected());
    this.$editAllCheckbox.prop('checked', this.allImagesSelected());
  }
};

/**
 * Returns true if any image is indeterminate
 */
yext.location.image.ImageListField.prototype.isIndeterminate = function() {
  for (var i = 0; i < this.images.length; i++) {
    if (this.images[i].isIndeterminate()) {
      return true;
    }
  }
  return false;
};

/**
 * Returns true if any display image is indeterminate
 */
yext.location.image.ImageListField.prototype.isDisplayIndeterminate = function() {
  for (var i = 0; i < this.displayImages.length; i++) {
    if (this.displayImages[i].isIndeterminate()) {
      return true;
    }
  }
  return false;
};

/**
 * Returns true if all images are checked
 */
yext.location.image.ImageListField.prototype.allImagesSelected = function() {
  for (var i = 0; i < this.images.length; i++) {
    if (!this.images[i].isSelected()) {
      return false;
    }
  }
  return true;
};

/**
 * Get the elided mode image count, if there is one
 */
yext.location.image.ImageListField.prototype.getElidedModeImageCountFromDOM = function() {
  var count = parseInt(this.$field.data('elidedModeImageCount'));
  if (!isNaN(count)) {
    return count;
  }
  // return undefined
};

/**
 * Bind the elided mode interaction
 */
yext.location.image.ImageListField.prototype.bindElidedMode = function() {
  var self = this;
  this.$field.find('.js-elided-mode-delete').click(function() {
    self.elidedModeImageCount = undefined;
    self.orderVaries = false;
    self.onUpdate();
  });
};

/**
 * Store the value of this field
 */
yext.location.image.ImageListField.prototype.storeValue = function() {
  this.storedValue = {
    images: this.images.map(function(image) {
      return image.getData();
    }),
    elidedModeImageCount: this.elidedModeImageCount,
    orderVaries: this.orderVaries,
  };
};

/**
 * Extract the image modules from their DOM elements
 * @return {!Array.<yext.location.image.Image>} List of images in this gallery
 */
yext.location.image.ImageListField.prototype.extractImages = function() {
  var self = this;
  var images = [];
  this.$field.find('.js-image-field').each(function() {
    var image = $(this).data('image');
    self.bindImage(image);
    images.push(image);
  });
  return images;
};

/**
 * Bind the image events
 * @param {yext.location.image.Image} image
 */
yext.location.image.ImageListField.prototype.bindImage = function(image) {
  var self = this;
  $(image).on('updated', function() {
    self.onUpdate();
  });
  $(image).on('delete', function() {
    image.$ctx.closest('li').remove();
    var index = self.images.indexOf(image);
    self.images.splice(index, 1);
    self.onUpdate();
  });
};

/**
 * Destinations are hidden when they are unset
 */
yext.location.image.ImageListField.prototype.bindShowDestinations = function() {
  var self = this;
  var $showDestinations = this.$field.find('.js-show-destinations');
  if ($showDestinations.length) {
    $showDestinations.click(function() {
      $showDestinations.hide();
      self.$field.find('.js-image-destinations').show();
    });
  }
};

/**
 * Bind the move image affordances using jQuery sort
 */
yext.location.image.ImageListField.prototype.bindMoveImage = function() {
  var self = this;
  this.$list.sortable({
    handle: '.js-move-image-affordance',
    axis: 'y',
    stop: function(e, ui) {
      self.reordered();
      // Note (dwen): Firefox sets top/left to 0 on stop, which causes the
      // image to be misaligned the next time it gets dragged. Explicitly
      // set top/left to auto to fix this.
      ui.item
        .css('top', 'auto')
        .css('left', 'auto');
    },
    containment: this.$list,
    tolerance: 'pointer',
  });
};

/**
 * Triggered when the gallery is reordered
 */
yext.location.image.ImageListField.prototype.reordered = function() {
  var images = [];
  this.$list.find('.js-image-field').each(function() {
    images.push($(this).data('image'));
  });
  this.images = images;
  this.orderVaries = false;
  this.onUpdate();
};

/**
 * Toggle the destinations on and off based on any being selected. This is separate from
 * standard render as it cannot be triggered by state change.
 */
yext.location.image.ImageListField.prototype.renderDestinations = function() {
  var $showDestinations = this.$field.find('.js-show-destinations');
  if ($showDestinations.length) {
    if (this.images.length > 0) {
      var hasDestinations = false;
      for (var i = 0; i < this.images.length; i++) {
        if (this.images[i].model.destinations.length > 0) {
          hasDestinations = true;
          break;
        }
      }
      $showDestinations.toggle(!hasDestinations);
      this.$field.find('.js-image-destinations').toggle(hasDestinations);
    } else {
      $showDestinations.hide();
    }
  }
};

/**
 * Bind new image and add to list and DOM
 */
yext.location.image.ImageListField.prototype.pushNewImage = function(image) {
  this.bindImage(image);
  this.images.push(image);
  this.$list.append(image.$ctx);
};

/**
 * When any updates are made to the field, this method is called
 */
yext.location.image.ImageListField.prototype.onUpdate = function() {
  this.emitUpdate();
  this.render();
};

/**
 * Any light UI changes needed when updates are made to the field
 */
yext.location.image.ImageListField.prototype.render = function() {
  this.$errorContainer.hide();
  this.$field.find('.js-show-destinations').toggle(this.images.length > 0);
  this.$editAllContainer.toggle(this.images.length > 1);
  this.$editAllContainer.find('.js-image-list-edit-all-text').html(this.renderEditAllMessage());
  this.$elidedModeContainer.toggle(!!this.elidedModeImageCount);
  this.$elidedModeContainer.find('.js-elided-mode-message')
    .html(this.renderElidedModeMessage());
  this.$field.find('.js-order-varies').toggle(this.orderVaries);
  this.$field.find('.js-add-image-button').toggle(this.images.length < this.maxItemsCount);
  this.syncCheckList();
};

/**
 * Return the correct I18n pluralization of the total number of images
 */
yext.location.image.ImageListField.prototype.renderEditAllMessage = function() {
  return yext.msgn('1 photo', '{0} photos', this.images.length);
};

/**
 * Return the correct I18n pluralization if elided mode, or empty
 */
yext.location.image.ImageListField.prototype.renderElidedModeMessage = function() {
  if (this.elidedModeImageCount) {
    return yext.msgn('1 Image', '{0} Images', this.elidedModeImageCount);
  }
  return '';
};

/**
 * Emit an updated message using jQuery pub/sub and the instance as context
 */
yext.location.image.ImageListField.prototype.emitUpdate = function() {
  $(this).trigger('updated');
};

/**
 * Reset the field to its stored state
 */
yext.location.image.ImageListField.prototype.reset = function() {
  this.set(this.storedValue);
};

/**
 * Set the field with new data
 * @param {!Object} field
 */
yext.location.image.ImageListField.prototype.set = function(field) {
  this.$list.empty();
  this.images = [];
  this.displayImages = [];

  if (field.images.length > 0) {
    for (var i = 0; i < field.images.length; i++) {
      var image = yext.location.image.Image.newImage(field.images[i], this.template);
      this.pushNewImage(image);
    }
    this.displayImages = this.images;
  } else if (field.displayImages && field.displayImages.length > 0) {
    for (var i = 0; i < field.displayImages.length; i++) {
      var image = yext.location.image.Image.newImage(field.displayImages[i], this.template);
      this.displayImages.push(image);
    }
  }

  this.elidedModeImageCount = field.elidedModeImageCount;
  this.displayElidedModeImageCount = field.displayElidedModeImageCount;
  this.orderVaries = Boolean(field.orderVaries);
  this.displayOrderVaries = Boolean(field.displayOrderVaries);

  if (field.timestamp) {
    this.timestamp = field.timestamp;
  }
  this.render();
  this.renderDestinations();
};

/**
 * Get the current state of the Field
 * NOTE(mattjs) This does not consider UI checkboxes being unselected
 */
yext.location.image.ImageListField.prototype.getData = function() {
  return {
    'images': this.images,
    'elidedModeImageCount': this.elidedModeImageCount,
    'timestamp': this.timestamp,
  };
};

yext.location.image.ImageListField.prototype.getJSONData = function() {
  let images = [];
  this.images.forEach(function(image) {
    let data = image.getData();
    if (image.model && image.model.base64Data) {
      data['base64Data'] = image.model.base64Data;
      data['url'] = undefined;
    }
    images.push(data);
  });

  return {
    'images': images,
    'elidedModeImageCount': this.elidedModeImageCount,
    'timestamp': this.timestamp,
  };
};

/**
 * @return {!jQuery} Display mode HTML for this field
 */
yext.location.image.ImageListField.prototype.getDisplay = function() {
  if (this.isDisplayElidedMode()) {
    return this.renderElidedModeMessage();
  } else if (this.isDisplayEmpty()) {
    return yext.location.helpers.emptyFieldMessage();
  } else if (this.isDisplayIndeterminate()) {
    return yext.location.helpers.variousContentMessage();
  } else {
    var items = [];
    for (var i = 0; i < this.displayImages.length; i++) {
      var $li = $('<li/>');
      $li.append(this.displayImages[i].getDisplay(this.viewTemplate, this.detailTemplate));
      items.push($li);
    }
    var $ul = $('<ul/>');
    $ul.append(items);
    new yext.location.image.ImageGallery($ul);
    return $ul;
  }
};

/**
 * Binds the asset selector to the add image button
 */
yext.location.image.ImageListField.prototype.bindAddImage = function() {
  var self = this;
  this.$field.find('.js-add-image-button').click(function() {
    self.showAssetSelector();
  });
};

/**
 * @return {boolean} True if there is a elidedModeImageCount
 */
yext.location.image.ImageListField.prototype.isElidedMode = function() {
  return this.elidedModeImageCount != undefined;
};

/**
 * @return {boolean} True if there is a displayElidedModeImageCount
 */
yext.location.image.ImageListField.prototype.isDisplayElidedMode = function() {
  return this.displayElidedModeImageCount != undefined;
};

/**
 * Show the asset selector for selecting images
 */
yext.location.image.ImageListField.prototype.showAssetSelector = function() {
  var self = this;
  var selectorUsage = goog.module.get('yext.dam.assetselector.usage');
  yext.dam.assetselector.events.show({
    type: yext.dam.assetselector.types.PHOTO,
    hideUploadImageTab: self.assetsOnly,
    includeUsedAssets: false,
    usageType: selectorUsage.forProfileFields(this.fieldId),
  },
  undefined,
  undefined,
  this.constraints);
  $(yext.dam.assetselector.events).on('added.assetselector', function(e, asset) {
    self.newImageFromAsset(asset);
  });
};

/**
 * @return {boolean} True if the field is empty
 */
yext.location.image.ImageListField.prototype.isEmpty = function() {
  return this.images.length === 0 && !this.isElidedMode();
};

/**
 * @return {boolean} True if the field doesn't have display image
 */
yext.location.image.ImageListField.prototype.isDisplayEmpty = function() {
  return this.displayImages.length === 0 && !this.isDisplayElidedMode();
};

/**
 * Generate an Image from an Asset and append to the list element
 * @param {yext.dam.asset.asset} asset
 */
yext.location.image.ImageListField.prototype.newImageFromAsset = function(asset) {
  if (typeof asset === 'undefined') {
    return;
  }

  var image = yext.location.image.Image.newImageFromAsset(asset, this.template);
  this.pushNewImage(image);
  this.render();
  $(this).trigger('newImageFromAsset');
};

/**
 * Gets any form data to merge in with the save request
 */
yext.location.image.ImageListField.prototype.getAnyFormData = function() {
  var $form = this.$ctx.find('form');
  if ($form.length) {
    return $form.serializeArray();
  }
  return [];
};

/**
 * Save this field
 * @param {function()} callback
 */
yext.location.image.ImageListField.prototype.save = function(callback, overrideUploadUrl, additionalFormData = []) {
  var self = this;
  var actualUploadUrl = overrideUploadUrl || this.uploadUrl;

  new yext.location.image.ImageUploader({
    uploadUrl: actualUploadUrl,
    fieldId: this.fieldId,
    encodedFieldId: this.encodedFieldId,
    images: this.images,
    fieldData: {
      'elidedModeImageCount': this.elidedModeImageCount,
      'orderVaries': this.orderVaries,
      'timestamp': this.timestamp,
    },
    otherData: this.getAnyFormData().concat(additionalFormData),
  }).upload(function(response) {
    // NOTE: Hack for custom goals
    if (response.responses) {
      callback(undefined, response);
      return;
    }
    if (!response.errors && !response.error) {
      self.reset();
    }
    self.postSave(response);
    self.onUploadSuccess(response, callback);
  }, function(response) {
    self.onUploadError(callback, undefined, response);
  });
};

/**
 * Set the field value post save and render display
 * @param {!Object} response
 */
yext.location.image.ImageListField.prototype.postSave = function(response) {
  $(this).trigger('saved', response);
  if (response['field']) {
    this.set(response['field']);
    this.storeValue();
  }
};

/**
 * Once save is successful, this method is called
 * @param {!Object} response
 * @param {function()} callback On save completed
 */
yext.location.image.ImageListField.prototype.onUploadSuccess = function(response, callback) {
  let errorMessage = !response['errors'] || response['errors'].length === 0
    ? undefined
    : JSON.parse(response['errors'])[0]['message'];
  if (errorMessage) {
    this.onUploadError(callback, errorMessage, response);
  } else if (response['error']) {
    this.onUploadError(callback, response['error'][this.fieldId], response);
  } else {
    callback();
  }
};

/**
 * When there is an error saving, this method is called
 * @param {!Object} response
 * @param {function()} callback On save completed
 */
yext.location.image.ImageListField.prototype.onUploadError = function(callback, msg, response) {
  msg = msg || yext.msg('There was an error while saving your Photo Gallery');
  callback(msg, response);
};

/**
 * Shared helper class for uploading image data
 *
 * @constructor
 */
yext.location.image.ImageUploader = function(config) {
  this.uploadUrl = config.uploadUrl;
  this.fieldId = config.fieldId;
  this.encodedFieldId = config.encodedFieldId;
  this.images = config.images;
  this.fieldData = yext.isObject(config.fieldData) ? config.fieldData : {};
  this.otherData = Array.isArray(config.otherData) ? config.otherData : [];
};

/**
 * Upload the images and react to success or failure
 *
 * @param {function()} success Callback on success
 * @param {function()} error Callback on error
 * @param {boolean} singleImage True if this is the single image field
 */
yext.location.image.ImageUploader.prototype.upload = function(success, error, singleImage) {
  var images = [];
  var files = [];
  var fileIndex = 0;

  for (var i = 0; i < this.images.length; i++) {
    // Check that this image is selected in the UI
    if (!this.images[i].isSelected()) {
      continue;
    }

    var image = this.images[i].model;
    var data = this.images[i].getData();
    if (image.file) {
      files.push(image.file);
      data['fileIndex'] = fileIndex++;
      data['url'] = undefined;
    } else if (image.base64Data) {
      data['base64Data'] = image.base64Data;
      data['url'] = undefined;
    }
    images.push(data);
  }

  if (singleImage) {
    if (images.length) {
      this.fieldData['image'] = images[0];
    }
  } else {
    this.fieldData['images'] = images;
  }

  var data = this.otherData.concat([{
    name: 'field',
    value: this.fieldId,
  }, {
    name: this.encodedFieldId,
    value: yext.JSON.stringify(this.fieldData),
  }]);

  var timeout = yext.dialog.showDelayedProgressDialog(yext.msg('Saving ...'));

  if (files.length) {
    yext.fileupload.upload({
      files: files,
      action: this.uploadUrl,
      data: data,
      done: function(response) {
        yext.dialog.hideDelayedProgressDialog(timeout);
        success(response);
      },
      error: function() {
        yext.dialog.hideDelayedProgressDialog(timeout);
        error();
      },
    });
  } else {
    $.ajax({
      type: 'POST',
      url: this.uploadUrl,
      data: data,
      success: function(response) {
        success(response);
      },
      error: function() {
        error();
      },
    }).always(function() {
      yext.dialog.hideDelayedProgressDialog(timeout);
    });
  }
};

/**
 * @constructor
 * @param {!jQuery} $ctx Display mode context
 */
yext.location.image.ImageGallery = function($ctx) {
  this.$ctx = $ctx;
  this.$images = $ctx.find('.image-border').addBack().filter('.image-border');
  this.$dialog = this.buildDialog();
  this.index = 0;
  this.bind();
  this.bindWarningIconHover();
};

/**
 * Build and bind the gallery dialog
 */
yext.location.image.ImageGallery.prototype.buildDialog = function() {
  var self = this;
  var $dialog = $($('.js-image-field-gallery-template').html());
  $dialog.dialog({
    autoOpen: false,
    modal: true,
    draggable: false,
    resizable: false,
    minWidth: 752,
    width: 'auto',
  });
  $dialog.find('.js-previous-image-button').click(function() {
    self.showPreviousImage();
  });
  $dialog.find('.js-next-image-button').click(function() {
    self.showNextImage();
  });
  return $dialog;
};

/**
 * Show the previous image
 */
yext.location.image.ImageGallery.prototype.showPreviousImage = function() {
  var previousIndex = this.index - 1;
  if (previousIndex < 0) {
    previousIndex = this.$images.length - 1;
  }
  this.showImage(previousIndex);
};

/**
 * Show the next image
 */
yext.location.image.ImageGallery.prototype.showNextImage = function() {
  var nextIndex = this.index + 1;
  if (nextIndex >= this.$images.length) {
    nextIndex = 0;
  }
  this.showImage(nextIndex);
};

/**
 * Bind image thumbnail clicks
 */
yext.location.image.ImageGallery.prototype.bind = function() {
  var self = this;
  this.$images.each(function(idx) {
    $(this).click(function(e) {
      e.stopPropagation();
      self.showImage(idx);
    });
  });
};


/**
 * Bind the warning icon upon hover for display images.
 */
yext.location.image.ImageGallery.prototype.bindWarningIconHover = function() {
  this.$ctx.find('.js-image-warning-icon').contextualHelp({
    basicText: yext.msg('This photo may be too small to display on certain publishers. ' +
      'Upload a photo that is at least 250x250 and 10 KB to ensure correct display.'),
    event: 'hover',
  });
};

/**
 * Show the image at the specified index
 *
 * @param {idx} Index for the image being shown
 */
yext.location.image.ImageGallery.prototype.showImage = function(idx) {
  var $image = $(this.$images.get(idx));
  var data = $image.data('image');
  var img = new Image();

  this.index = idx;
  img.src = yext.getDynamicThumbnailUrl(data.url, 700, 450);

  this.$dialog.find('.js-image-field-gallery-content').empty().append(img);
  this.$dialog.dialog({
    'title': this.getTitle(),
  });
  this.$dialog.dialog('open');
};

/**
 * @return {string} The current title of the gallery
 */
yext.location.image.ImageGallery.prototype.getTitle = function() {
  return yext.msg('Photo {0} of {1}', this.index + 1, this.$images.length);
};
